import React, { useCallback, useState } from 'react';
import { useCableState } from './hooks/useCableState';
import { useCableSelection } from './hooks/useCableSelection';
import { useAuth } from './context/AuthContext';
import { useUserSettings } from './context/UserSettingsContext';
import { Icons } from './components/icons/Icons';
import StandardCableForm from './components/forms/StandardCableForm';
import MultipolarCableForm from './components/forms/MultipolarCableForm';
import CableCatalogSelector from './components/forms/CableCatalogSelector';
import { UnifiedCablesList } from './components/visualization/UnifiedCablesList';
import { getMultipolarLabel, normalizeStandardCable } from './utils/cableLogic';
import { validateCableFields, validateCable } from './utils/validation';
import { insertCableInOrder, validateCableFields as validateCableFieldsUtil } from './utils/cableInsertion';
import { autoAdjustTerminalsForSectionChange } from './utils/terminalCompatibility';
import GeneralSettingsSidebar from './components/ui/GeneralSettingsSidebar';
import QuantitiesForm from './components/forms/QuantitiesForm';
import SchematicCableView from './components/visualization/SchematicCableView';
import { LoginModal } from './components/auth/LoginModal';
import { RegisterModal } from './components/auth/RegisterModal';
import { UserSettingsPage } from './components/settings/UserSettingsPage';
import { LanguageToggle } from './components/ui/LanguageToggle';
import { useI18n } from './context/I18nContext';
import LandingPage from './components/ui/LandingPage';
import { handleFileImport } from './utils/fileOperations';
import {
  assignCatalogItemToCable,
  findCatalogItemForCable,
  reconcileCableWithCatalog,
  reconcileCablesWithCatalog,
  unlinkCableFromCatalog
} from './utils/cableCatalog';

const EMPTY_CABLE_CATALOG = { unipolari: [], multipolari: [] };

function App() {
  const { t, language } = useI18n();
  const { isAuthenticated, isLoading: authLoading, logout, user } = useAuth();
  const { settings: userSettings } = useUserSettings();
  const [showLogin, setShowLogin] = useState(true);
  const [appMode, setAppMode] = useState('landing');
  const [activeTab, setActiveTab] = useState('workspace');

  const { state, actions } = useCableState();
  const {
    setSettings,
    setTerminali,
    setCables,
    setCurrentCable,
    setCurrentMultipolarCable
  } = actions;
  const {
    isSelectionModeEnabled,
    setIsSelectionModeEnabled,
    selectedCables,
    toggleCableSelection,
    selectAllCables,
    deselectAllCables
  } = useCableSelection(state?.cables || []);

  const [warnings, setWarnings] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [keepAllSettings, setKeepAllSettings] = useState(true);
  const [showDeleteButtons, setShowDeleteButtons] = useState(new Set());
  const [, setScrollToCable] = useState({ siglatura: null, originalIndex: null });
  const [hoveredCable, setHoveredCable] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (state.settings.lingua !== language) {
      setSettings((prev) => ({ ...prev, lingua: language }));
    }
  }, [language, state.settings.lingua, setSettings]);

  React.useEffect(() => {
    if (isAuthenticated) {
      setShowLoginModal(false);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (userSettings?.terminal_data && Object.keys(userSettings.terminal_data).length > 0) {
      setTerminali(userSettings.terminal_data);
    }
  }, [userSettings?.terminal_data, setTerminali]);

  React.useEffect(() => {
    if (userSettings?.machine_settings) {
      setSettings((prev) => (
        prev.machine_settings === userSettings.machine_settings
          ? prev
          : {
              ...prev,
              machine_settings: userSettings.machine_settings
            }
      ));
    }
  }, [userSettings?.machine_settings, setSettings]);

  React.useEffect(() => {
    const userPreference = userSettings?.default_settings?.app_settings?.enableSeparateSingleCableSiglature;
    if (userPreference !== undefined) {
      setSettings((prev) => (
        prev.enableSeparateSingleCableSiglature === !!userPreference
          ? prev
          : {
              ...prev,
              enableSeparateSingleCableSiglature: !!userPreference
            }
      ));
    }
  }, [userSettings?.default_settings?.app_settings?.enableSeparateSingleCableSiglature, setSettings]);

  React.useEffect(() => {
    if (userSettings?.profile_data) {
      setSettings((prev) => (
        prev.profile_data === userSettings.profile_data
          ? prev
          : {
              ...prev,
              profile_data: userSettings.profile_data
            }
      ));
    }
  }, [userSettings?.profile_data, setSettings]);

  const cableCatalog = userSettings?.default_settings?.cable_catalog || EMPTY_CABLE_CATALOG;
  const assignableCableCatalog = React.useMemo(() => {
    const allItems = [...(cableCatalog.unipolari || []), ...(cableCatalog.multipolari || [])];
    const counts = allItems.reduce((result, item) => {
      const key = String(item?.codiceInterno || '').trim().toLocaleLowerCase();
      if (key) result[key] = (result[key] || 0) + 1;
      return result;
    }, {});
    const filterAssignable = (items = []) => items.filter((item) => {
      const key = String(item?.codiceInterno || '').trim().toLocaleLowerCase();
      return key && counts[key] === 1;
    });
    return {
      unipolari: filterAssignable(cableCatalog.unipolari),
      multipolari: filterAssignable(cableCatalog.multipolari)
    };
  }, [cableCatalog]);

  React.useEffect(() => {
    setCables((previous) => {
      const reconciled = reconcileCablesWithCatalog(previous, cableCatalog);
      return JSON.stringify(reconciled) === JSON.stringify(previous) ? previous : reconciled;
    });
    setCurrentCable((previous) => previous?.catalogAssignment
      ? reconcileCableWithCatalog(previous, cableCatalog)
      : previous);
    setCurrentMultipolarCable((previous) => previous?.catalogAssignment
      ? reconcileCableWithCatalog(previous, cableCatalog)
      : previous);
  }, [cableCatalog, setCables, setCurrentCable, setCurrentMultipolarCable]);

  const handleAssignCurrentCatalogItem = useCallback((item) => {
    if (state.cableMode === 'standard') {
      setCurrentCable((previous) => {
        const assigned = assignCatalogItemToCable(previous, item);
        return autoAdjustTerminalsForSectionChange({
          cable: assigned,
          cables: state.cables,
          editingIndex: state.editingIndex,
          terminali: state.terminali,
          settings: state.settings
        });
      });
      return;
    }

    const currentPoles = state.currentMultipolarCable?.poli || [];
    const targetCount = Math.max(0, Number.parseInt(item?.numeroPoli, 10) || 0);
    if (targetCount > 0 && targetCount < currentPoles.length) {
      const confirmed = window.confirm(t('cableForm.confirmCatalogPoleReduction', { count: currentPoles.length - targetCount }));
      if (!confirmed) return;
    }
    setCurrentMultipolarCable((previous) => {
      const assigned = assignCatalogItemToCable(previous, item, { forceStructure: true });
      return autoAdjustTerminalsForSectionChange({
        cable: assigned,
        cables: state.cables,
        editingIndex: state.editingMultipolarIndex,
        terminali: state.terminali,
        settings: state.settings
      });
    });
  }, [state.cableMode, state.currentMultipolarCable?.poli, state.cables, state.editingIndex, state.editingMultipolarIndex, state.terminali, state.settings, setCurrentCable, setCurrentMultipolarCable, t]);

  const handleUnlinkCurrentCatalogItem = useCallback(() => {
    if (state.cableMode === 'standard') {
      setCurrentCable((previous) => unlinkCableFromCatalog(previous));
    } else {
      setCurrentMultipolarCable((previous) => unlinkCableFromCatalog(previous));
    }
  }, [state.cableMode, setCurrentCable, setCurrentMultipolarCable]);

  const handleRealignCurrentCatalogItem = useCallback(() => {
    const currentCable = state.currentMultipolarCable;
    const item = findCatalogItemForCable(currentCable, cableCatalog);
    if (item) handleAssignCurrentCatalogItem(item);
  }, [state.currentMultipolarCable, cableCatalog, handleAssignCurrentCatalogItem]);

  const toggleDeleteButton = useCallback((originalIndex) => {
    setShowDeleteButtons((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(originalIndex)) {
        newSet.delete(originalIndex);
      } else {
        newSet.add(originalIndex);
      }
      return newSet;
    });
  }, []);

  const addMorsettoDirection = useCallback((field, direction, poloId) => {
    const arrow = direction === 'up' ? '▲' : '▼';

    if (typeof poloId === 'number') {
      actions.setCurrentMultipolarCable((prev) => ({
        ...prev,
        poli: prev.poli.map((polo) => {
          if (polo.id === poloId) {
            const currentText = polo[field] || '';
            const newText = `${currentText} ${arrow}`.trim();
            return { ...polo, [field]: newText };
          }
          return polo;
        })
      }));
    } else {
      actions.setCurrentCable((prev) => ({
        ...prev,
        [field]: `${prev[field] || ''} ${arrow}`.trim()
      }));
    }
  }, [actions]);

  const addVirtualConnection = useCallback((field, poloId) => {
    const virtualDot = '·';

    if (typeof poloId === 'number') {
      actions.setCurrentMultipolarCable((prev) => ({
        ...prev,
        poli: prev.poli.map((polo) => {
          if (polo.id === poloId) {
            const currentText = polo[field] || '';
            const newText = `${currentText}${virtualDot}`;
            return { ...polo, [field]: newText };
          }
          return polo;
        })
      }));
    } else {
      actions.setCurrentCable((prev) => ({
        ...prev,
        [field]: `${prev[field] || ''}${virtualDot}`
      }));
    }
  }, [actions]);

  const handleStandardCableInputChange = useCallback((field, value) => {
    actions.setCurrentCable((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'sezione') {
        return autoAdjustTerminalsForSectionChange({
          cable: next,
          cables: state.cables,
          editingIndex: state.editingIndex,
          terminali: state.terminali,
          settings: state.settings
        });
      }
      const canUseSeparateSiglature = state.settings.enableSeparateSingleCableSiglature || prev.useSeparateSiglature;

      if (!canUseSeparateSiglature && (field === 'siglaturaA' || field === 'siglaturaB')) {
        next.siglaturaA = value;
        next.siglaturaB = value;
        next.useSeparateSiglature = false;
        return next;
      }

      if (field === 'siglaturaA' || field === 'siglaturaB') {
        next.useSeparateSiglature = String(next.siglaturaA || '').trim() !== String(next.siglaturaB || '').trim();
      }

      return next;
    });
  }, [actions, state.cables, state.editingIndex, state.terminali, state.settings]);

  const handleMultipolarPoleUpdate = useCallback((poloId, field, value) => {
    if (field !== 'sezione') {
      actions.updatePolo(poloId, field, value);
      return;
    }

    actions.setCurrentMultipolarCable((prev) => {
      const next = {
        ...prev,
        poli: prev.poli.map(polo => polo.id === poloId ? { ...polo, sezione: value } : polo)
      };
      return autoAdjustTerminalsForSectionChange({
        cable: next,
        cables: state.cables,
        editingIndex: state.editingMultipolarIndex,
        terminali: state.terminali,
        settings: state.settings
      });
    });
  }, [actions, state.cables, state.editingMultipolarIndex, state.terminali, state.settings]);

  const handleAddMultipolarCable = useCallback(() => {
    const cable = state.currentMultipolarCable;
    const missingFields = validateCableFieldsUtil(cable, state.cables, language);

    if (missingFields.length > 0) {
      const nice = missingFields.join(', ');
      setWarnings([t('validation.multipolarCableInsertedMissingFields', { fields: nice })]);
    }

    let finalCable = { ...cable };
    if (!finalCable.nomeCavo || finalCable.nomeCavo.trim() === '') {
      finalCable.nomeCavo = `${t('cableForm.multipolarCable')} ${getMultipolarLabel(finalCable.poli, state.settings?.useAWG)}`;
    }

    if (state.editingMultipolarIndex >= 0) {
      actions.setCables((prev) => {
        const updated = [...prev];
        updated[state.editingMultipolarIndex] = { ...finalCable, originalIndex: state.editingMultipolarIndex };
        return updated;
      });
      actions.setEditingMultipolarIndex(-1);
      actions.resetCurrentMultipolarCable();
      setShowDeleteButtons(new Set());
    } else {
      actions.addMultipolarCable(finalCable);
      const updatedCables = insertCableInOrder(finalCable, state.cables, state.settings);
      actions.setCables(updatedCables);
      actions.resetCurrentMultipolarCable();
    }
  }, [state.currentMultipolarCable, state.cables, state.settings, state.editingMultipolarIndex, actions, language, t]);

  const handleCancelEdit = useCallback(() => {
    actions.resetCurrentMultipolarCable();
    actions.setEditingMultipolarIndex(-1);
    setShowDeleteButtons(new Set());
  }, [actions]);

  const handleAddStandardCable = useCallback(() => {
    const missingFields = validateCableFields(state.currentCable, state.cables, state.settings.lingua);

    if (missingFields.length > 0) {
      const nice = missingFields.join(', ');
      setWarnings([t('validation.cableInsertedMissingFields', { fields: nice })]);
    }

    if (state.editingIndex >= 0) {
      const updatedCables = [...state.cables];
      updatedCables[state.editingIndex] = {
        ...normalizeStandardCable(state.currentCable),
        originalIndex: state.editingIndex
      };
      actions.setCables(updatedCables);
      actions.setEditingIndex(-1);
      setShowDeleteButtons(new Set());
    } else {
      const updatedCables = insertCableInOrder(normalizeStandardCable(state.currentCable), state.cables, state.settings);
      actions.setCables(updatedCables);
    }

    if (!keepAllSettings) {
      actions.resetCurrentCable();
    }
  }, [state.currentCable, state.cables, state.editingIndex, state.settings, actions, keepAllSettings, t]);

  const swapAB = useCallback((poloId = null) => {
    if (poloId) {
      actions.setCurrentMultipolarCable((prev) => {
        const updatedPoli = prev.poli.map((polo) => {
          if (polo.id === poloId) {
            return {
              ...polo,
              siglaturaA: polo.siglaturaB,
              siglaturaB: polo.siglaturaA,
              terminaleA: polo.terminaleB,
              terminaleB: polo.terminaleA,
              collegamentoA: polo.collegamentoB,
              collegamentoB: polo.collegamentoA
            };
          }
          return polo;
        });
        return { ...prev, poli: updatedPoli };
      });
    } else {
      actions.setCurrentCable((prev) => ({
        ...prev,
        siglaturaA: prev.siglaturaB,
        siglaturaB: prev.siglaturaA,
        terminaleA: prev.terminaleB,
        terminaleB: prev.terminaleA,
        collegamentoA: prev.collegamentoB,
        collegamentoB: prev.collegamentoA
      }));
    }
  }, [actions]);

  const handleSaveAsNewStandardCable = useCallback(() => {
    const missingFields = validateCableFields(state.currentCable, state.cables, state.settings.lingua);

    if (missingFields.length > 0) {
      setWarnings((prev) => [
        ...prev,
        `Attenzione: inserire ${missingFields.join(' e ')} per salvare come nuovo cavo.`
      ]);
    }

    const updatedCables = insertCableInOrder(normalizeStandardCable(state.currentCable), state.cables, state.settings);
    actions.setCables(updatedCables);
    actions.setEditingIndex(-1);
    setShowDeleteButtons(new Set());

    if (!keepAllSettings) {
      actions.resetCurrentCable();
    }
  }, [state.currentCable, state.cables, state.settings, actions, keepAllSettings]);

  const handleEditStandardCable = useCallback((index) => {
    const cableToEdit = state.cables[index];
    if (!cableToEdit) return;

    setActiveTab('workspace');
    actions.setCurrentCable(normalizeStandardCable({ ...cableToEdit }));
    actions.setEditingIndex(index);
    actions.setEditingMultipolarIndex(-1);
    actions.setCableMode('standard');

    setTimeout(() => {
      const formElement = document.querySelector('#cable-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        formElement.style.transition = 'all 0.3s';
        formElement.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
        setTimeout(() => {
          formElement.style.boxShadow = '';
        }, 1500);
      }
    }, 100);
  }, [state.cables, actions]);

  const handleDeleteStandardCable = useCallback((index) => {
    actions.setCables((prev) => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((cable, newIndex) => ({
        ...cable,
        originalIndex: newIndex
      }));
    });
    actions.setEditingIndex(-1);
    actions.setEditingMultipolarIndex(-1);
    setShowDeleteButtons(new Set());
  }, [actions]);

  const handleEditMultipolarCable = useCallback((index) => {
    const cableToEdit = state.cables[index];
    if (!cableToEdit) return;

    setActiveTab('workspace');
    actions.setCurrentMultipolarCable(JSON.parse(JSON.stringify(cableToEdit)));
    actions.setEditingMultipolarIndex(index);
    actions.setEditingIndex(-1);
    actions.setCableMode('multipolar');

    setTimeout(() => {
      const formElement = document.querySelector('#cable-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        formElement.style.transition = 'all 0.3s';
        formElement.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
        setTimeout(() => {
          formElement.style.boxShadow = '';
        }, 1500);
      }
    }, 100);
  }, [state.cables, actions]);

  const handleDeleteMultipolarCable = useCallback((index) => {
    actions.setCables((prev) => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((cable, newIndex) => ({
        ...cable,
        originalIndex: newIndex
      }));
    });
    actions.setEditingMultipolarIndex(-1);
    actions.setEditingIndex(-1);
    setShowDeleteButtons(new Set());
  }, [actions]);

  const handleSaveAsNewMultipolarCable = useCallback(() => {
    const cable = state.currentMultipolarCable;
    let finalCable = { ...cable };

    if (!finalCable.nomeCavo || finalCable.nomeCavo.trim() === '') {
      finalCable.nomeCavo = `${t('cableForm.multipolarCable')} ${getMultipolarLabel(finalCable.poli, state.settings?.useAWG)}`;
    }

    const updatedCables = insertCableInOrder(finalCable, state.cables, state.settings);
    actions.setCables(updatedCables);
    actions.setEditingMultipolarIndex(-1);
    actions.resetCurrentMultipolarCable();
    setShowDeleteButtons(new Set());
  }, [state.currentMultipolarCable, state.cables, state.settings, actions, t]);

  const handleValidateCable = useCallback((cable) => {
    const originalCable = cable.originalIndex !== undefined
      ? state.cables.find((c) => c.originalIndex === cable.originalIndex)
      : cable;

    return validateCable(originalCable || cable, state.cables, language, state.settings?.useAWG, state.settings);
  }, [state.cables, language, state.settings]);

  const handleStartNewProject = useCallback(() => {
    actions.setCables([]);
    actions.setAvailableSheaths([]);
    actions.setSettings((prev) => ({
      ...prev,
      quantitaCablaggi: 1,
      commessaId: '',
      commessaDate: '',
      marcaturePerCollegamento: false
    }));
    actions.setFileName('');
    actions.setRevision(0);
    actions.setSchemaElettrico('');
    actions.setInstallazione('');
    actions.setGroupTitles({});
    setActiveTab('workspace');
    setAppMode('app');
  }, [actions]);

  const handleSettingsClick = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      setShowLogin(true);
    } else {
      setShowSettingsPanel(true);
    }
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
    setShowLogin(true);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('cableList.loading')}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'project',
      label: language === 'it' ? 'Progetto' : 'Project'
    },
    {
      id: 'workspace',
      label: language === 'it' ? 'Workspace Cavi' : 'Cable Workspace',
      badge: state.cables.length > 0 ? state.cables.length : null
    },
    {
      id: 'quantities',
      label: language === 'it' ? 'Quantità' : 'Quantities',
      badge: `x${state.settings.quantitaCablaggi || 1}`,
      disabled: state.cables.length === 0
    },
    {
      id: 'schematic',
      label: language === 'it' ? 'Schema' : 'Schematic',
      disabled: state.cables.length === 0
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100 font-sans flex flex-col">
      <div className="relative z-50 w-full bg-black shadow-lg py-3 px-3 sm:px-6 flex items-center justify-between">
        <div
          className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setAppMode('landing')}
          title="Torna alla Home"
        >
          <Icons.Logo className="h-12 w-auto" />
          <h1 className="ml-4 text-white text-2xl font-bold">WireCraft</h1>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          <LanguageToggle />
          {isAuthenticated ? (
            <div className="relative group">
              <button className="flex items-center space-x-2 text-white hover:text-gray-300 transition-colors">
                <Icons.User className="w-6 h-6" />
                <span className="hidden text-sm font-medium md:inline">{userSettings?.profile_data?.username || user?.email}</span>
                <Icons.ChevronDown className="w-4 h-4" />
              </button>

              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button
                    onClick={handleSettingsClick}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Icons.Settings className="w-5 h-5" />
                    <span>{t('common.settings')}</span>
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={logout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <Icons.X className="w-5 h-5" />
                    <span>{t('settings.logout')}</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSettingsClick}
                className="text-white hover:text-gray-300 transition-colors p-2 rounded-md hover:bg-gray-800"
                title={t('settings.settingsRequiresLogin')}
              >
                <Icons.Settings className="w-6 h-6" />
              </button>
              <button
                onClick={handleLoginClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Accedi
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {appMode === 'landing' ? (
          <LandingPage
            isMobile={isMobile}
            onStartNew={handleStartNewProject}
            onImport={(e) => {
              handleFileImport(
                e,
                actions.setCables,
                setWarnings,
                actions.setSettings,
                actions.setRevision,
                actions.setSchemaElettrico,
                actions.setInstallazione,
                actions.setFileName,
                actions.setGroupTitles,
                actions.setListSortMode,
                setSuccessMessage,
                actions.setAvailableSheaths,
                cableCatalog
              );
              setActiveTab('workspace');
              setAppMode('app');
            }}
          />
        ) : (
          <main className="p-6 lg:p-8 animate-fade-in-up">
            <div className="max-w-7xl mx-auto mb-6 sticky top-4 z-40">
              <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 p-2 overflow-x-auto">
                <div className="flex items-center gap-2 min-w-max">
                  {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;

                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => !tab.disabled && setActiveTab(tab.id)}
                        disabled={tab.disabled}
                        className={`flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${isActive
                          ? 'bg-blue-600 text-white shadow-sm'
                          : tab.disabled
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        <span>{tab.label}</span>
                        {tab.badge ? (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'}`}>
                            {tab.badge}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {activeTab === 'project' && (
              <div className="max-w-7xl mx-auto bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('settings.generalSettings')}</h2>
                <GeneralSettingsSidebar
                  settings={state.settings}
                  setSettings={actions.setSettings}
                  currentCable={state.currentCable}
                  setCurrentCable={actions.setCurrentCable}
                  cables={state.cables}
                  setCables={actions.setCables}
                  setFileName={actions.setFileName}
                  setRevision={actions.setRevision}
                  setSchemaElettrico={actions.setSchemaElettrico}
                  setInstallazione={actions.setInstallazione}
                />
              </div>
            )}

            {activeTab === 'workspace' && (
              <div className="max-w-7xl mx-auto">
                {warnings.length > 0 && (
                  <div className="mb-6 bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-3 rounded relative flex items-start justify-between" role="alert">
                    <div className="pr-4">
                      <strong className="font-bold">{t('messages.warning')}</strong>
                      <ul className="list-disc list-inside mt-2">
                        {warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                    <button
                      className="ml-4 text-yellow-800 hover:text-yellow-900"
                      onClick={() => setWarnings([])}
                      aria-label={t('messages.closeWarning')}
                    >
                      ×
                    </button>
                  </div>
                )}

                {successMessage && (
                  <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">{t('messages.success')}</strong>
                    <p className="text-sm">{successMessage}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <div id="cable-form" className="bg-white p-6 rounded-lg shadow-md">
                      <div className="mb-6">
                        <CableCatalogSelector
                          cable={state.cableMode === 'standard' ? state.currentCable : state.currentMultipolarCable}
                          items={state.cableMode === 'standard' ? assignableCableCatalog.unipolari : assignableCableCatalog.multipolari}
                          useAWG={state.settings.useAWG}
                          isMultipolar={state.cableMode === 'multipolar'}
                          onAssign={handleAssignCurrentCatalogItem}
                          onUnlink={handleUnlinkCurrentCatalogItem}
                          onRealign={state.cableMode === 'multipolar' ? handleRealignCurrentCatalogItem : undefined}
                          headerContent={(
                            <h2 className="text-xl font-semibold text-gray-800">
                              {state.cableMode === 'standard'
                                ? state.editingIndex >= 0
                                  ? t('cableForm.editStandardCable')
                                  : t('cableForm.newCable')
                                : state.editingMultipolarIndex >= 0
                                  ? t('cableForm.editMultipolarCable')
                                  : t('cableForm.newCable')}
                            </h2>
                          )}
                          trailingControl={(
                            <select
                              value={state.cableMode}
                              onChange={(e) => actions.setCableMode(e.target.value)}
                              disabled={state.editingIndex >= 0 || state.editingMultipolarIndex >= 0}
                              className={`px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 shadow-sm flex-shrink-0 ${state.editingIndex >= 0 || state.editingMultipolarIndex >= 0 ? 'opacity-60 cursor-not-allowed' : ''
                                }`}
                            >
                              <option value="standard">{t('cableForm.standardCable')}</option>
                              <option value="multipolar">{t('cableForm.multipolarCable')}</option>
                            </select>
                          )}
                        />
                      </div>

                      {(state.editingIndex >= 0 || state.editingMultipolarIndex >= 0) && (
                        <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                          {t('settings.modeLockedDuringEdit')}
                        </div>
                      )}

                      {state.cableMode === 'standard' ? (
                        <StandardCableForm
                          currentCable={state.currentCable}
                          handleInputChange={handleStandardCableInputChange}
                          settings={state.settings}
                          terminali={state.terminali}
                          cables={state.cables}
                          addCable={handleAddStandardCable}
                          saveAsNewCable={handleSaveAsNewStandardCable}
                          editingIndex={state.editingIndex}
                          cancelEdit={() => actions.setEditingIndex(-1)}
                          keepAllSettings={keepAllSettings}
                          setKeepAllSettings={setKeepAllSettings}
                          setSuccessMessage={setSuccessMessage}
                          addMorsettoDirection={addMorsettoDirection}
                          addVirtualConnection={addVirtualConnection}
                          swapAB={swapAB}
                          availableSheaths={state.availableSheaths}
                          onAddSheath={(sheath) => actions.setAvailableSheaths((prev) => [...prev, sheath])}
                          onDeleteSheath={(sheathId) => {
                            actions.setAvailableSheaths((prev) => prev.filter((s) => s.id !== sheathId));
                            actions.setCables((prev) => prev.map((cable) => (
                              cable.guaina === sheathId ? { ...cable, guaina: null } : cable
                            )));
                          }}
                        />
                      ) : (
                        <MultipolarCableForm
                          currentMultipolarCable={state.currentMultipolarCable}
                          setCurrentMultipolarCable={actions.setCurrentMultipolarCable}
                          settings={state.settings}
                          onAddMultipolarCable={handleAddMultipolarCable}
                          onSaveAsNewMultipolarCable={handleSaveAsNewMultipolarCable}
                          editingMultipolarIndex={state.editingMultipolarIndex}
                          onCancelEdit={handleCancelEdit}
                          terminalData={state.terminali}
                          cables={state.cables}
                          addPolo={actions.addPolo}
                          removePolo={actions.removePolo}
                          updatePolo={handleMultipolarPoleUpdate}
                          duplicatePolo={actions.duplicatePolo}
                          addMorsettoDirection={addMorsettoDirection}
                          addVirtualConnection={addVirtualConnection}
                          swapAB={swapAB}
                          setSuccessMessage={setSuccessMessage}
                          availableSheaths={state.availableSheaths}
                          onAddSheath={(sheath) => actions.setAvailableSheaths((prev) => [...prev, sheath])}
                          onDeleteSheath={(sheathId) => {
                            actions.setAvailableSheaths((prev) => prev.filter((s) => s.id !== sheathId));
                            actions.setCables((prev) => prev.map((cable) => (
                              cable.guaina === sheathId ? { ...cable, guaina: null } : cable
                            )));
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="bg-white p-6 rounded-lg shadow-md h-full flex flex-col">
                      <UnifiedCablesList
                        cables={state.cables}
                        setCables={actions.setCables}
                        setWarnings={setWarnings}
                        editCable={handleEditStandardCable}
                        editMultipolarCable={handleEditMultipolarCable}
                        deleteCable={handleDeleteStandardCable}
                        deleteMultipolarCable={handleDeleteMultipolarCable}
                        settings={{ ...state.settings, setIsDragModeEnabled: (val) => actions.setSettings((prev) => ({ ...prev, isDragModeEnabled: val })) }}
                        setScrollToCable={setScrollToCable}
                        hoveredCable={hoveredCable}
                        setHoveredCable={setHoveredCable}
                        isSelectionModeEnabled={isSelectionModeEnabled}
                        setIsSelectionModeEnabled={setIsSelectionModeEnabled}
                        selectedCables={selectedCables}
                        toggleCableSelection={toggleCableSelection}
                        selectAllCables={selectAllCables}
                        deselectAllCables={deselectAllCables}
                        editingIndex={state.editingIndex}
                        editingMultipolarIndex={state.editingMultipolarIndex}
                        validateCable={handleValidateCable}
                        setSettings={actions.setSettings}
                        setRevision={actions.setRevision}
                        setSchemaElettrico={actions.setSchemaElettrico}
                        setInstallazione={actions.setInstallazione}
                        setFileName={actions.setFileName}
                        setGroupTitles={actions.setGroupTitles}
                        setSuccessMessage={setSuccessMessage}
                        setListSortMode={actions.setListSortMode}
                        terminali={state.terminali}
                        availableSheaths={state.availableSheaths}
                        setAvailableSheaths={actions.setAvailableSheaths}
                        cableCatalog={cableCatalog}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'quantities' && state.cables.length > 0 && (
              <div className="max-w-7xl mx-auto">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <QuantitiesForm
                    cables={state.cables}
                    settings={state.settings}
                    terminali={state.terminali}
                    availableSheaths={state.availableSheaths}
                    setWarnings={setWarnings}
                    setSuccessMessage={setSuccessMessage}
                  />
                </div>
              </div>
            )}

            {activeTab === 'schematic' && state.cables.length > 0 && (
              <div className="max-w-7xl mx-auto">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('schematic.schematicViewTitle')}</h2>
                  <SchematicCableView
                    cables={state.cables}
                    settings={state.settings}
                    setListSortMode={actions.setListSortMode}
                    hoveredCable={hoveredCable}
                    setHoveredCable={setHoveredCable}
                    isMobile={isMobile}
                    editCable={handleEditStandardCable}
                    editMultipolarCable={handleEditMultipolarCable}
                    deleteCable={handleDeleteStandardCable}
                    deleteMultipolarCable={handleDeleteMultipolarCable}
                    validateCable={handleValidateCable}
                    editingIndex={state.editingIndex}
                    editingMultipolarIndex={state.editingMultipolarIndex}
                    showDeleteButtons={showDeleteButtons}
                    toggleDeleteButton={toggleDeleteButton}
                    isSelectionModeEnabled={isSelectionModeEnabled}
                    selectedCables={selectedCables}
                    availableSheaths={state.availableSheaths}
                  />
                </div>
              </div>
            )}
          </main>
        )}
      </div>

      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowLoginModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <Icons.X className="w-6 h-6" />
            </button>
            <div className="p-6">
              {showLogin ? (
                <LoginModal onSwitchToRegister={() => setShowLogin(false)} />
              ) : (
                <RegisterModal onSwitchToLogin={() => setShowLogin(true)} />
              )}
            </div>
          </div>
        </div>
      )}

      {showSettingsPanel && (
        <UserSettingsPage onClose={() => setShowSettingsPanel(false)} />
      )}

      <footer className="w-full bg-white shadow-inner py-4 px-6 text-center text-gray-500 text-sm">
        <p>WireCraft © 2026, by BPA Electric</p>
      </footer>
    </div>
  );
}

export default App;
